import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { PreviewSummaryCards } from './PreviewSummaryCards';
import { PreviewEmployeeTable } from './PreviewEmployeeTable';
import { Loader2, AlertCircle, FileText, AlertTriangle } from 'lucide-react';
import type { PayrollPreviewResponse } from '@/services/payroll/payrollPreviewService';

interface PayrollPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  previewData: PayrollPreviewResponse | null;
  isLoading: boolean;
  onConfirm: () => void;
  isGenerating: boolean;
}

export const PayrollPreviewDialog = ({
  open,
  onOpenChange,
  previewData,
  isLoading,
  onConfirm,
  isGenerating,
}: PayrollPreviewDialogProps) => {
  const { currencyCode } = useWorkplaceCurrency();
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const skippedCount = previewData?.skipped_employees?.length || 0;
  const zeroPayCount = previewData?.zero_pay_employees?.length || 0;
  const eligibleCount = previewData?.totals.total_employees || 0;
  // Denominator must reflect everything the operator selected, including
  // zero-pay employees, otherwise the button understates the original selection.
  const totalSelected = eligibleCount + skippedCount + zeroPayCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Payroll Preview
          </DialogTitle>
          <DialogDescription>
            Review the payroll details before generating payslips. This is what will be created.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-3 text-muted-foreground">Generating preview...</span>
          </div>
        ) : previewData ? (
          <div className="space-y-6 overflow-y-auto flex-1">
            {/* Warning for employees with no compensation rules — no payslip will be created */}
            {zeroPayCount > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {zeroPayCount} Employee{zeroPayCount > 1 ? 's Have' : ' Has'} No Compensation Rules
                </AlertTitle>
                <AlertDescription>
                  <p className="mb-2">
                    The following employee{zeroPayCount > 1 ? 's have' : ' has'} no active compensation rules
                    for this period. <strong>No payslip will be created for them.</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    {previewData?.zero_pay_employees?.map((emp) => (
                      <li key={emp.profile_id} className="text-sm">
                        <strong>{emp.first_name} {emp.last_name}</strong> ({emp.email})
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-sm">
                    Assign active compensation rules to these employees, then re-run the preview.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {/* Warning for skipped employees */}
            {skippedCount > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {skippedCount} Employee{skippedCount > 1 ? 's' : ''} Will Be Skipped
                </AlertTitle>
                <AlertDescription>
                  <p className="mb-2">
                    The following employee{skippedCount > 1 ? 's already have' : ' already has'} a payslip for this period:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    {previewData.skipped_employees?.map((emp) => (
                      <li key={emp.profile_id} className="text-sm">
                        <strong>{emp.first_name} {emp.last_name}</strong> ({emp.email})
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-sm">
                    To regenerate payslips for these employees, first roll back or delete their existing payslips.
                  </p>
                </AlertDescription>
              </Alert>
            )}

            {/* Informational warning for pending leave requests */}
            {previewData.has_pending_leave_warnings && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Pending Leave Requests</AlertTitle>
                <AlertDescription>
                  Some employees have pending leave requests during this pay period that are not yet approved.
                  These are not included in the paid leave calculations. Review and approve them before generating payroll if needed.
                </AlertDescription>
              </Alert>
            )}

            {/* Service charge distribution failure warning */}
            {previewData.service_charge_warning && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Service Charge Warning</AlertTitle>
                <AlertDescription>
                  {previewData.service_charge_warning}
                </AlertDescription>
              </Alert>
            )}

            {previewData.service_charge_period && !previewData.service_charge_warning && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Service charge distribution will be applied from period:{' '}
                  <strong>{previewData.service_charge_period.period_label}</strong> (Pool:{' '}
                  {formatCurrency(previewData.service_charge_period.total_pool_amount)})
                </AlertDescription>
              </Alert>
            )}

            {eligibleCount > 0 ? (
              <>
                <div>
                  <h3 className="text-lg font-semibold mb-4">Summary</h3>
                  <PreviewSummaryCards totals={previewData.totals} />
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Employee Breakdown</h3>
                  <PreviewEmployeeTable employees={previewData.employees} />
                </div>

                <Alert variant="default">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Important:</strong> Once you confirm, {eligibleCount}{' '}
                    payslip(s) will be generated and employees will be able to view them immediately.
                    {skippedCount > 0 && ` (${skippedCount} will be skipped due to existing payslips.)`}
                    {' '}This action can be reversed using the rollback feature if needed.
                  </AlertDescription>
                </Alert>
              </>
            ) : (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>No Payslips to Generate</AlertTitle>
                <AlertDescription>
                  All selected employees already have payslips for this period. No new payslips will be created.
                </AlertDescription>
              </Alert>
            )}
          </div>
        ) : (
          <div className="py-12 text-center text-muted-foreground">
            No preview data available
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isGenerating}>
            Cancel
          </Button>
          <Button 
            onClick={onConfirm} 
            disabled={!previewData || isGenerating || eligibleCount === 0}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : eligibleCount === 0 ? (
              'No Payslips to Generate'
            ) : totalSelected > eligibleCount ? (
              `Generate ${eligibleCount} of ${totalSelected} Payslip(s)`
            ) : (
              `Confirm & Generate ${eligibleCount} Payslip(s)`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
